/**
 * Back to top button
 * - Does not touch core theme logic
 * - Uses minimal DOM + passive scroll listener
 */
(function () {
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  ready(function () {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;

    const SHOW_AFTER_PX = 400;
    let ticking = false;

    function setVisible(visible) {
      if (visible) {
        btn.hidden = false;
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
        // wait for transition to finish before hiding for a11y/tab order
        window.setTimeout(() => {
          if (!btn.classList.contains('is-visible')) btn.hidden = true;
        }, 200);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        setVisible(window.scrollY > SHOW_AFTER_PX);
      });
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        window.scrollTo({ top: 0, left: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();

    // Sticky buy button on product pages: keep floating buttons clear of it
    const stickyBuy = document.querySelector('.wt-product__sticky-buy');
    if (stickyBuy) {
      const BODY_CLASS = 'wt-has-sticky-buy';
      const ACTIVE_CLASS = 'wt-product__sticky-buy--show';

      // Override default behavior: footer should NOT hide sticky buy.
      // We drive visibility ONLY by whether the real add-to-cart module is visible.
      const addToCartModule = document.querySelector('.wt-product__add-to-cart');
      let shouldShowStickyByPosition = true;

      // Mobile behavior:
      // - show only after user scrolled a bit
      // - hide on scroll up, show on scroll down
      const mobileMQ = window.matchMedia ? window.matchMedia('(max-width: 767px)') : { matches: false };
      const MOBILE_STICKY_START_PX = 250;
      let lastScrollY = window.scrollY || 0;
      let isScrollingDown = true;

      const computeFinalShouldShow = () => {
        const y = window.scrollY || 0;
        if (mobileMQ.matches) {
          if (y <= MOBILE_STICKY_START_PX) return false;
          return shouldShowStickyByPosition && isScrollingDown;
        }
        return shouldShowStickyByPosition;
      };

      const applySticky = () => {
        const finalShouldShow = computeFinalShouldShow();
        stickyBuy.classList.toggle(ACTIVE_CLASS, finalShouldShow);
      };

      if (addToCartModule) {
        const observer = new IntersectionObserver((entries) => {
          const entry = entries && entries[0];
          if (!entry) return;
          shouldShowStickyByPosition = !entry.isIntersecting;
          applySticky();
        });

        // delay a tick so core observers can attach first
        window.setTimeout(() => {
          observer.observe(addToCartModule);
        }, 0);
      }

      // Track scroll direction on mobile (throttled)
      let stickyScrollTicking = false;
      const onStickyScroll = () => {
        if (stickyScrollTicking) return;
        stickyScrollTicking = true;
        window.requestAnimationFrame(() => {
          const y = window.scrollY || 0;
          // tiny delta to avoid jitter on iOS
          if (y > lastScrollY + 2) isScrollingDown = true;
          else if (y < lastScrollY - 2) isScrollingDown = false;
          lastScrollY = y;
          applySticky();
          stickyScrollTicking = false;
        });
      };
      window.addEventListener('scroll', onStickyScroll, { passive: true });

      const sync = () => {
        document.body.classList.toggle(BODY_CLASS, stickyBuy.classList.contains(ACTIVE_CLASS));
      };

      const updateStickyHeightVar = () => {
        const isVisible = stickyBuy.classList.contains(ACTIVE_CLASS);
        if (!isVisible) {
          document.body.style.setProperty('--wt-sticky-buy-height', '0px');
          return;
        }
        const h = Math.ceil(stickyBuy.getBoundingClientRect().height || 0);
        document.body.style.setProperty('--wt-sticky-buy-height', `${h}px`);
      };

      sync();
      updateStickyHeightVar();

      const mo = new MutationObserver(() => {
        // If theme core toggles the class, enforce our rules.
        const finalShouldShow = computeFinalShouldShow();
        if (finalShouldShow && !stickyBuy.classList.contains(ACTIVE_CLASS)) stickyBuy.classList.add(ACTIVE_CLASS);
        if (!finalShouldShow && stickyBuy.classList.contains(ACTIVE_CLASS)) stickyBuy.classList.remove(ACTIVE_CLASS);
        sync();
        updateStickyHeightVar();
      });
      mo.observe(stickyBuy, { attributes: true, attributeFilter: ['class'] });

      // Keep height variable in sync with layout changes (variants wrapping etc.)
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => {
          updateStickyHeightVar();
        });
        ro.observe(stickyBuy);
      }

      window.addEventListener(
        'resize',
        () => {
          updateStickyHeightVar();
          applySticky();
        },
        { passive: true },
      );

      // Sticky variants: proxy selection to the original variant-options radios
      const stickyVariantSelects = Array.from(
        stickyBuy.querySelectorAll('[data-sticky-option-position]'),
      );
      const originalVariantOptions = document.querySelector('variant-options.wt-variant-options');
      const sectionId = originalVariantOptions?.dataset?.section;

      const getMainAddBtn = () => {
        const selector = sectionId
          ? `#product-form-${CSS.escape(sectionId)} [name="add"]`
          : '[id^="product-form-"] [name="add"]';
        return document.querySelector(selector);
      };

      const stickyPriceEl = stickyBuy.querySelector('[data-sticky-price]');
      const stickyPriceMobileEl = stickyBuy.querySelector('[data-sticky-price-mobile]');
      const getMainPriceEl = () => {
        if (sectionId) return document.getElementById(`price-${sectionId}`);
        return document.querySelector('[id^="price-"]');
      };

      const syncStickyPriceFromMain = () => {
        const mainPrice = getMainPriceEl();
        if (!mainPrice) return;
        if (stickyPriceEl) stickyPriceEl.innerHTML = mainPrice.innerHTML;
        if (stickyPriceMobileEl) stickyPriceMobileEl.innerHTML = mainPrice.innerHTML;
        updateStickyHeightVar();
      };

      const syncStickyAddBtnFromMain = () => {
        const stickyAddBtn = stickyBuy.querySelector('.wt-product__sticky-buy__button');
        if (!stickyAddBtn) return;
        const mainAdd = getMainAddBtn();
        if (!mainAdd) return;

        const mainTextEl = mainAdd.querySelector('span');
        const stickyTextEl = stickyAddBtn.querySelector('span');
        const mainText = (mainTextEl?.textContent || '').trim();

        if (stickyTextEl && mainText && stickyTextEl.textContent.trim() !== mainText) {
          stickyTextEl.textContent = mainText;
        }

        const isDisabled = mainAdd.hasAttribute('disabled');
        stickyAddBtn.toggleAttribute('disabled', isDisabled);
        if (mainText) stickyAddBtn.setAttribute('aria-label', mainText);
      };

      const setStickyVariantSelectsFromOriginal = () => {
        if (!originalVariantOptions || stickyVariantSelects.length === 0) return;
        stickyVariantSelects.forEach((sel) => {
          const pos = sel.getAttribute('data-sticky-option-position');
          const checked = originalVariantOptions.querySelector(
            `input[data-position="${pos}"]:checked`,
          );
          if (checked && String(sel.value) !== String(checked.value)) {
            sel.value = checked.value;
          }
        });
      };

      if (originalVariantOptions && stickyVariantSelects.length) {
        stickyVariantSelects.forEach((sel) => {
          sel.addEventListener('change', () => {
            const pos = sel.getAttribute('data-sticky-option-position');
            const val = sel.value;
            const input = originalVariantOptions.querySelector(
              `input[data-position="${pos}"][value="${CSS.escape(val)}"]`,
            );
            if (input) input.click();
          });
        });

        // initial + sync on variant changes
        setStickyVariantSelectsFromOriginal();
        syncStickyPriceFromMain();
        syncStickyAddBtnFromMain();
        originalVariantOptions.addEventListener('variantChangeEnd', setStickyVariantSelectsFromOriginal);
        originalVariantOptions.addEventListener('variantChangeEnd', syncStickyPriceFromMain);
        originalVariantOptions.addEventListener('variantChangeEnd', syncStickyAddBtnFromMain);
        originalVariantOptions.addEventListener('change', setStickyVariantSelectsFromOriginal);
        originalVariantOptions.addEventListener('change', syncStickyPriceFromMain);
        originalVariantOptions.addEventListener('change', syncStickyAddBtnFromMain);
      }

      // Sticky add-to-cart: trigger the real form submit button (keeps theme logic intact)
      const stickyAddBtn = stickyBuy.querySelector('.wt-product__sticky-buy__button');

      if (stickyAddBtn) {
        stickyAddBtn.addEventListener(
          'click',
          (e) => {
            // capture click before theme's pdp.js scroll handler
            e.preventDefault();
            e.stopImmediatePropagation();

            const mainAdd = getMainAddBtn();
            if (mainAdd && !mainAdd.hasAttribute('disabled')) {
              mainAdd.click();
              return;
            }

            // if can't add yet (no valid variant), scroll to the real options / add-to-cart area
            if (addToCartModule) {
              addToCartModule.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          },
          true,
        );

        // initial sync + keep in sync if theme updates main button state/text
        syncStickyAddBtnFromMain();
        const mainAdd = getMainAddBtn();
        if (mainAdd && window.MutationObserver) {
          const textEl = mainAdd.querySelector('span');
          const btnObserver = new MutationObserver(syncStickyAddBtnFromMain);
          btnObserver.observe(mainAdd, { attributes: true, attributeFilter: ['disabled'] });
          if (textEl) {
            btnObserver.observe(textEl, { childList: true, characterData: true, subtree: true });
          }
        }
      }

      // Keep sticky price synced if theme updates price DOM
      const mainPriceEl = getMainPriceEl();
      if (mainPriceEl) {
        syncStickyPriceFromMain();
        if (window.MutationObserver) {
          const priceObserver = new MutationObserver(syncStickyPriceFromMain);
          priceObserver.observe(mainPriceEl, { childList: true, subtree: true });
        }
      }

      // Sync sticky quantity counter with the real product form quantity input
      const stickyQty = stickyBuy.querySelector('[data-sticky-quantity] .js-counter-quantity');
      const mainQty = document.querySelector('.wt-product__add-to-cart .js-counter-quantity');

      if (stickyQty && mainQty) {
        const toInt = (v) => {
          const n = parseInt(String(v || '').replace(/[^\d]/g, ''), 10);
          return Number.isFinite(n) && n > 0 ? n : 1;
        };

        const syncStickyToMain = () => {
          const val = toInt(stickyQty.value);
          if (String(mainQty.value) !== String(val)) {
            mainQty.value = String(val);
            mainQty.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };

        const syncMainToSticky = () => {
          const val = toInt(mainQty.value);
          if (String(stickyQty.value) !== String(val)) {
            stickyQty.value = String(val);
          }
        };

        // init
        syncMainToSticky();

        stickyQty.addEventListener('change', syncStickyToMain);
        stickyQty.addEventListener('input', syncStickyToMain);

        mainQty.addEventListener('change', syncMainToSticky);
      }
    }
  });
})();
